import { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { AnomalyState } from './anomalyReducer';
import type { UploadProgress } from '@/shared/components/feedback/ProgressTracker';
import { log } from '@/core/debug/anomalyLogger';
import { translateMessage } from '@/core/services/upload/utils';

interface UploadHookSlice {
  uploading: boolean;
  progress: number;
  error: string | null;
}

interface Args {
  state: AnomalyState;
  upload: UploadHookSlice;
  /** Real-time data from SocketIO; populated in Task 2. Undefined for now. */
  socket?: {
    progress?: number;
    message?: string;
    etaFormatted?: string;
    step?: string;
    messageKey?: string;
    messageParams?: Record<string, unknown>;
    currentStep?: number;
    totalSteps?: number;
  };
}

export function useAnomalyProgress({ state, upload, socket }: Args): UploadProgress | null {
  const { t } = useTranslation('dataProcessing');
  const lastLoggedSig = useRef<string>('');

  return useMemo(() => {
    // Backend ships authoritative currentStep/totalSteps in the anomaly_progress
    // payload (see plan 2026-05-15-anomaly-progress-overlay-parity Task 2). Until
    // the first event lands, fall back to a one-per-major-stage estimate.
    const stlEnabled = !!state.params?.stl?.run;
    const lstmEnabled = !!state.params?.lstm?.run;
    const fallbackTotalSteps = 1 + (stlEnabled ? 1 : 0) + (lstmEnabled ? 1 : 0);
    const totalSteps = socket?.totalSteps ?? fallbackTotalSteps;

    const translatedMessage = socket?.messageKey
      ? translateMessage(
          socket.messageKey,
          socket.messageParams,
          socket.message,
          'dataProcessing.processingMessages',
        )
      : socket?.message;

    let result: UploadProgress | null;
    let branch: string;

    if (upload.uploading) {
      branch = 'uploading_chunks';
      result = {
        fileName: state.filename ?? '',
        uploadProgress: upload.progress,
        processingProgress: 0,
        status: 'uploading',
        currentStep: 1,
        totalSteps,
      };
    } else if (state.status === 'uploading') {
      // After chunk upload finishes the FSM enters 'uploading' while POST /load
      // round-trips on the backend (CSV parse + initial plot generation). Without
      // this branch the overlay would vanish between chunk-end and LOADED dispatch,
      // making the page look frozen.
      branch = 'fsm_uploading_load_roundtrip';
      result = {
        fileName: state.filename ?? '',
        uploadProgress: 100,
        processingProgress: 0,
        status: 'processing',
        message: t('processingMessages.analyzingFile', { defaultValue: 'Analyzing file...' }),
        currentStep: 1,
        totalSteps,
      };
    } else if (state.status === 'running') {
      branch = 'running';
      result = {
        fileName: state.filename ?? '',
        uploadProgress: 100,
        processingProgress: socket?.progress ?? 0,
        status: 'processing',
        message: translatedMessage,
        etaFormatted: socket?.etaFormatted,
        step: socket?.step,
        currentStep: socket?.currentStep ?? 1,
        totalSteps,
      };
    } else if (state.status === 'applying_stl') {
      branch = 'applying_stl';
      result = {
        fileName: state.filename ?? '',
        uploadProgress: 100,
        processingProgress: socket?.progress ?? 0,
        status: 'processing',
        message: translatedMessage,
        etaFormatted: socket?.etaFormatted,
        step: socket?.step,
        currentStep: socket?.currentStep ?? 2,
        totalSteps,
      };
    } else if (state.status === 'applying_lstm') {
      branch = 'applying_lstm';
      // If STL was disabled, LSTM is step 2; otherwise step 3.
      result = {
        fileName: state.filename ?? '',
        uploadProgress: 100,
        processingProgress: socket?.progress ?? 0,
        status: 'processing',
        message: translatedMessage,
        etaFormatted: socket?.etaFormatted,
        step: socket?.step,
        currentStep: socket?.currentStep ?? (stlEnabled ? 3 : 2),
        totalSteps,
      };
    } else {
      branch = 'null (overlay hidden)';
      result = null;
    }

    // De-duplicate log calls caused by React StrictMode double-renders in dev.
    // Build a signature from the computed output; only log when it changes.
    const sig =
      result === null
        ? `null:${state.status}`
        : `${result.status}:up=${result.uploadProgress}:proc=${result.processingProgress}:step=${result.currentStep}/${result.totalSteps}`;

    if (sig !== lastLoggedSig.current) {
      lastLoggedSig.current = sig;
      if (result === null) {
        log.hook('useAnomalyProgress', `branch=${branch}`, { status: state.status });
      } else {
        log.hook('useAnomalyProgress', `branch=${branch}`, { socketProgress: socket?.progress });
      }
    }

    return result;
  }, [
    state.status,
    state.filename,
    state.params?.stl?.run,
    state.params?.lstm?.run,
    upload.uploading,
    upload.progress,
    socket?.progress,
    socket?.message,
    socket?.etaFormatted,
    socket?.step,
    socket?.messageKey,
    socket?.messageParams,
    socket?.currentStep,
    socket?.totalSteps,
    t,
  ]);
}
